import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { ALL_DAYS_OFF, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'

/** One automatic retry after a failed/cancelled parent-preview partial reload. */
const PARENT_PREVIEW_MAX_AUTO_RETRIES = 1

export interface UseFollowParentSchedulePreviewArgs {
  /** When false (Follow parent OFF, or an edit surface with a locked parent), no partial reloads run. */
  enabled: boolean
  /** Selected parent entity id (site / department / unit). */
  parentId: number | null
  parentDaySchedule: WeekdaySchedule | undefined
  /** Seeded create/edit already has a matching server preview — skip the first reload. */
  initialParentId?: number | null
  /** Query param key for Inertia partial reload (`site_id`, `department_id`, `unit_id`). */
  reloadParamKey: string
  /**
   * Bump to force a re-fetch after exhausted retries without requiring Follow OFF→ON.
   * Departments use this so toggling one nested unit recovers while others stay Follow ON.
   */
  retryNonce?: number
}

export interface UseFollowParentSchedulePreviewResult {
  effectiveParentPreview: WeekdaySchedule | undefined
  parentPreviewPending: boolean
  /** True after auto-retries are exhausted — preview is a neutral placeholder, not the parent week. */
  parentPreviewFailed: boolean
}

interface ParentDaySchedulePageProps {
  parentDaySchedule?: WeekdaySchedule
}

/**
 * Live follow-parent day-schedule preview. Partial-reloads `parentDaySchedule` when
 * the selected parent id changes, without wiping unrelated form fields.
 * Shared by Units (department→site), Departments (site), and Instruments (unit).
 */
export const useFollowParentSchedulePreview = ({
  enabled,
  parentId,
  parentDaySchedule,
  initialParentId = null,
  reloadParamKey,
  retryNonce = 0,
}: UseFollowParentSchedulePreviewArgs): UseFollowParentSchedulePreviewResult => {
  // `undefined` = unsynced (Follow OFF mid-reload / failed reload) — forces refetch on re-enable.
  const previewedParentIdRef = useRef<number | null | undefined>(initialParentId)
  const resolvedParentPreviewParentIdRef = useRef<number | null>(initialParentId)
  const parentPreviewCancelRef = useRef<{ cancel: () => void } | null>(null)
  const parentPreviewRequestGenerationRef = useRef(0)
  const parentPreviewPendingRef = useRef(false)
  const parentPreviewAutoRetryCountRef = useRef(0)
  // After auto-retries are exhausted, stop claiming "Loading…" (nothing in flight).
  const parentPreviewRetriesExhaustedRef = useRef(false)
  const isMountedRef = useRef(true)
  const [parentPreviewPending, setParentPreviewPending] = useState(false)
  const [stableParentPreview, setStableParentPreview] = useState(parentDaySchedule)

  const clearParentPreviewPending = () => {
    parentPreviewPendingRef.current = false
    if (isMountedRef.current) setParentPreviewPending(false)
  }

  const markParentPreviewPending = () => {
    parentPreviewPendingRef.current = true
    if (isMountedRef.current) setParentPreviewPending(true)
  }

  // Mirror props only while safe. Skip when: reload in flight, retries exhausted,
  // onSuccess already trust-resolved this parent (lagging prop must not clobber), or we
  // are awaiting a different parent (prior parent's prop must not refill stable).
  // Cleared parent (AC6): keep local ALL_DAYS_ON — do not re-absorb a stale prior-site prop
  // left in Inertia page state after we skipped the blank-id partial reload.
  useEffect(() => {
    if (parentPreviewPendingRef.current) return
    if (parentPreviewRetriesExhaustedRef.current) return
    if (parentId == null) {
      setStableParentPreview(ALL_DAYS_ON)
      return
    }
    if (resolvedParentPreviewParentIdRef.current === parentId) return
    if (
      resolvedParentPreviewParentIdRef.current != null &&
      resolvedParentPreviewParentIdRef.current !== parentId
    ) {
      return
    }
    setStableParentPreview(parentDaySchedule)
  }, [parentDaySchedule, parentId])

  // Cancel only on unmount. Do not cancel via the reload effect's cleanup — React Strict
  // Mode runs that cleanup then re-invokes the effect, which would early-return on the
  // already-updated ref and never refetch parentDaySchedule.
  // Mark unmounted before cancel so onCancel/onError cannot auto-retry into a dead tree.
  // Reset in-flight markers after cancel: onCancel is ignored while unmounted, which
  // would otherwise leave previewedParentIdRef advanced and block the Strict Mode
  // remount effect from starting a fresh reload (endless pending).
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      parentPreviewCancelRef.current?.cancel()
      parentPreviewCancelRef.current = null
      if (parentPreviewPendingRef.current) {
        previewedParentIdRef.current = undefined
        parentPreviewPendingRef.current = false
      }
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      // Follow OFF mid-reload may have advanced previewedParentIdRef without a matching
      // parentDaySchedule response. Cancel + invalidate so re-enable refetches.
      // Idle OFF (no in-flight reload) keeps the synced ref so re-enable skips a redundant visit.
      const hadInFlightParentPreviewReload = parentPreviewPendingRef.current
      parentPreviewRequestGenerationRef.current += 1
      parentPreviewCancelRef.current?.cancel()
      parentPreviewCancelRef.current = null
      if (hadInFlightParentPreviewReload) {
        previewedParentIdRef.current = undefined
        // Drop prior parent's resolved identity — cancel ignores onCancel via generation bump.
        resolvedParentPreviewParentIdRef.current = null
        parentPreviewRetriesExhaustedRef.current = false
        setStableParentPreview(undefined)
      } else if (parentPreviewRetriesExhaustedRef.current) {
        // Idle Follow OFF after exhausted retries — clear so re-enable recovers without
        // requiring a parentId change (Instrument Types Add Instrument path).
        parentPreviewRetriesExhaustedRef.current = false
        previewedParentIdRef.current = undefined
        resolvedParentPreviewParentIdRef.current = null
        setStableParentPreview(undefined)
      }
      clearParentPreviewPending()
      return
    }

    const selectedParentId = parentId
    // retryNonce bump after exhaustion must not early-return on a still-matching parent id.
    if (
      previewedParentIdRef.current === selectedParentId &&
      !parentPreviewRetriesExhaustedRef.current
    )
      return

    previewedParentIdRef.current = selectedParentId
    parentPreviewAutoRetryCountRef.current = 0
    parentPreviewRetriesExhaustedRef.current = false
    const requestGeneration = ++parentPreviewRequestGenerationRef.current
    parentPreviewCancelRef.current?.cancel()
    parentPreviewCancelRef.current = null

    if (selectedParentId == null) {
      // Cleared parent → all-on locally (AC6). Skip partial reload with '' — redundant
      // after local ALL_DAYS_ON and can race with a later parent select.
      resolvedParentPreviewParentIdRef.current = null
      setStableParentPreview(ALL_DAYS_ON)
      clearParentPreviewPending()
      return
    }

    if (resolvedParentPreviewParentIdRef.current !== selectedParentId) {
      // Pending until this parent's schedule arrives; effectiveParentPreview must not
      // surface a prior parent's week (A→B) — grid shows loading / ALL_DAYS_OFF.
      markParentPreviewPending()
    }

    const startParentPreviewReload = (reloadGeneration: number) => {
      router.reload({
        only: ['parentDaySchedule'],
        data: { [reloadParamKey]: selectedParentId },
        onCancelToken: (cancelToken) => {
          parentPreviewCancelRef.current = cancelToken
        },
        onSuccess: (page) => {
          if (!isMountedRef.current) return
          if (parentPreviewRequestGenerationRef.current !== reloadGeneration) return
          const nextParentDaySchedule = (page.props as ParentDaySchedulePageProps).parentDaySchedule
          // Null/missing schedule must not mark this parent resolved — that would show a
          // stale stableParentPreview as if it belonged to the new selection.
          if (nextParentDaySchedule == null) {
            leaveParentPreviewUnsyncedAndMaybeRetry(reloadGeneration)
            return
          }
          parentPreviewRetriesExhaustedRef.current = false
          setStableParentPreview(nextParentDaySchedule)
          resolvedParentPreviewParentIdRef.current = selectedParentId
          clearParentPreviewPending()
        },
        onCancel: () => {
          // Ignore stale cancel from a superseded request (rapid parent changes).
          if (!isMountedRef.current) return
          if (parentPreviewRequestGenerationRef.current !== reloadGeneration) return
          leaveParentPreviewUnsyncedAndMaybeRetry(reloadGeneration)
        },
        onError: () => {
          if (!isMountedRef.current) return
          if (parentPreviewRequestGenerationRef.current !== reloadGeneration) return
          leaveParentPreviewUnsyncedAndMaybeRetry(reloadGeneration)
        },
      })
    }

    // Drop prior-parent stable preview and force a re-render. Do not clear-then-mark
    // pending around auto-retry — React may bail out when pending ends true→true and
    // leave a stale resolvedParentId read from the previous render.
    const leaveParentPreviewUnsyncedAndMaybeRetry = (failedGeneration: number) => {
      previewedParentIdRef.current = undefined
      resolvedParentPreviewParentIdRef.current = null
      setStableParentPreview(undefined)
      if (!maybeAutoRetryParentPreview(failedGeneration)) {
        // Nothing left in flight — show neutral empty week, not endless "Loading…".
        parentPreviewRetriesExhaustedRef.current = true
        setStableParentPreview(ALL_DAYS_OFF)
        clearParentPreviewPending()
      }
    }

    const maybeAutoRetryParentPreview = (failedGeneration: number): boolean => {
      if (!isMountedRef.current) return false
      if (parentPreviewRequestGenerationRef.current !== failedGeneration) return false
      if (parentPreviewAutoRetryCountRef.current >= PARENT_PREVIEW_MAX_AUTO_RETRIES) return false

      parentPreviewAutoRetryCountRef.current += 1
      previewedParentIdRef.current = selectedParentId
      markParentPreviewPending()
      const retryGeneration = ++parentPreviewRequestGenerationRef.current
      startParentPreviewReload(retryGeneration)
      return true
    }

    startParentPreviewReload(requestGeneration)
  }, [enabled, parentId, reloadParamKey, retryNonce])

  const resolvedParentId = resolvedParentPreviewParentIdRef.current
  const previewResolvedForSelectedParent = parentId != null && resolvedParentId === parentId
  const parentPreviewRetriesExhausted = parentPreviewRetriesExhaustedRef.current
  // After error/cancel, resolved may still point at a prior parent — never show that
  // schedule as if it belonged to the current selection. Exhausted retries settle to
  // ALL_DAYS_OFF without keeping the loading affordance forever.
  const awaitingTrustedParentPreview =
    enabled &&
    parentId != null &&
    !previewResolvedForSelectedParent &&
    !parentPreviewRetriesExhausted

  const effectiveParentPreview = (() => {
    if (!enabled) return parentDaySchedule
    if (parentId == null) return ALL_DAYS_ON
    if (parentPreviewRetriesExhausted) return ALL_DAYS_OFF
    // While pending (or after error/cancel), never show another parent's stable week.
    // undefined + parentPreviewPending → WeeklyScheduleGrid ALL_DAYS_OFF loading state.
    if (!previewResolvedForSelectedParent) return undefined
    // Prefer stable so onSuccess can apply the schedule before Inertia props catch up.
    return stableParentPreview ?? parentDaySchedule
  })()

  return {
    effectiveParentPreview,
    parentPreviewPending:
      (parentPreviewPending || awaitingTrustedParentPreview) && enabled && parentId != null,
    parentPreviewFailed: parentPreviewRetriesExhausted && enabled && parentId != null,
  }
}
